import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import toast from '../utils/toast';

const AgentNotificationContext = createContext();

export const useAgentNotifications = () => {
  const context = useContext(AgentNotificationContext);
  if (!context) {
    throw new Error('useAgentNotifications must be used within an AgentNotificationProvider');
  }
  return context;
};

export const AgentNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useUser();

  const baseUrl = '/api/notifications'; // Agent notifications endpoint

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL}${baseUrl}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user, baseUrl]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}${baseUrl}/unread-count`, {
        credentials: 'include',
      });

      // Handle 401 gracefully - user not authenticated yet
      if (response.status === 401) {
        setUnreadCount(0);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch unread count');
      }

      const data = await response.json();
      setUnreadCount(data.count || data.unreadCount || 0);
    } catch (error) {
      // Silently handle errors - don't spam console for expected auth failures
      if (error.message && !error.message.includes('401')) {
        console.error('Error fetching unread count:', error);
      }
      setUnreadCount(0);
    }
  }, [user, baseUrl]);

  // Server-Sent Events connection using native EventSource API
  const connectToNotificationStream = useCallback(() => {
    // Close any existing connection first
    if (window.notificationStreamController) {
      console.log('🔔 Closing existing connection before creating new one...');
      window.notificationStreamController.abort();
      window.notificationStreamController = null;
    }
    
    if (isConnected) {
      console.log('🔔 Already connected, skipping connection attempt');
      return;
    }

    if (!user) {
      console.log('🔔 No user, skipping SSE connection');
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      console.error('🔔 VITE_API_URL is not set, cannot connect to notification stream');
      return;
    }

    try {
      const streamUrl = `${apiUrl}${baseUrl}/stream`;
      console.log('🔔 Connecting to notification stream:', streamUrl);
      
      // Create EventSource with credentials for CORS
      // Note: EventSource doesn't support custom headers, so auth must be via cookies
      const eventSource = new EventSource(streamUrl, {
        withCredentials: true
      });

      eventSource.onopen = () => {
        console.log('🔔 Connected to notification stream');
        setIsConnected(true);
      };

      eventSource.onerror = (error) => {
        const readyState = eventSource.readyState;
        console.error('🔔 SSE connection error:', error);
        console.error('🔔 EventSource readyState:', readyState);
        
        // EventSource.CONNECTING = 0, EventSource.OPEN = 1, EventSource.CLOSED = 2
        if (readyState === EventSource.CLOSED) {
          setIsConnected(false);
          eventSource.close();
          
          // Clear the stored connection
          if (window.notificationStreamController?.eventSource === eventSource) {
            window.notificationStreamController = null;
          }
          
          // Only reconnect if user is still logged in and not already reconnecting
          if (user && !isConnected) {
            console.log('🔔 Connection closed, attempting to reconnect in 5 seconds...');
            setTimeout(() => {
              if (user && !isConnected) {
                connectToNotificationStream();
              }
            }, 5000);
          }
        } else if (readyState === EventSource.CONNECTING) {
          // Still connecting, don't treat as error yet
          console.log('🔔 SSE still connecting...');
        }
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🔔 SSE message received:', data);

          switch (data.type) {
            case 'connection':
              console.log('🔔 Notification stream connected:', data.message);
              break;
            
            case 'heartbeat':
              // Keep connection alive
              break;
            
            case 'new_notification':
              console.log('🔔 New notification received:', data.notification);
              // Add new notification to the list
              setNotifications(prev => [data.notification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Show toast notification
              toast.success(`New notification: ${data.notification.title}`);
              break;
            
            case 'notification_update':
              console.log('🔔 Notification update received:', data);
              if (data.updateType === 'read') {
                // Mark notification as read in local state
                setNotifications(prev => 
                  prev.map(notification => 
                    notification.notificationid === data.notificationId
                      ? { ...notification, is_read: true }
                      : notification
                  )
                );
              } else if (data.updateType === 'deleted') {
                // Remove notification from local state
                setNotifications(prev => 
                  prev.filter(notification => notification.notificationid !== data.notificationId)
                );
              } else if (data.updateType === 'unread_count') {
                // Update unread count
                setUnreadCount(data.data.count);
              }
              break;
            
            default:
              console.log('🔔 Unknown SSE message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      
      // Store connection for cleanup
      window.notificationStreamController = { 
        eventSource, 
        abort: () => eventSource.close() 
      };
      
    } catch (error) {
      console.error('🔔 SSE connection error:', error);
      setIsConnected(false);
      
      // Only reconnect if user is still logged in
      if (user) {
        console.log('🔔 Will attempt to reconnect in 5 seconds...');
        setTimeout(() => {
          if (user && !isConnected) {
            connectToNotificationStream();
          }
        }, 5000);
      }
    }
  }, [user, isConnected, baseUrl]);

  // Disconnect from notification stream
  const disconnectFromNotificationStream = useCallback(() => {
    if (window.notificationStreamController) {
      window.notificationStreamController.abort();
      window.notificationStreamController = null;
      setIsConnected(false);
      console.log('🔔 Disconnected from notification stream');
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}${baseUrl}/${notificationId}/read`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.notificationid === notificationId 
            ? { ...notification, is_read: true }
            : notification
        )
      );

      // Refresh unread count from server
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  }, [baseUrl, fetchUnreadCount]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}${baseUrl}/read-all`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );

      // Refresh unread count from server
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  }, [baseUrl, fetchUnreadCount]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}${baseUrl}/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }

      // Update local state
      setNotifications(prev => 
        prev.filter(notification => notification.notificationid !== notificationId)
      );

      // Refresh unread count from server
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  }, [baseUrl, fetchUnreadCount]);

  // Initial fetch and SSE connection (only when user is available)
  useEffect(() => {
    if (!user) return;
    
    console.log('🔔 useEffect triggered - fetching notifications and connecting to stream');
    
    // Fetch data and connect when user is available
    fetchNotifications();
    fetchUnreadCount();
    connectToNotificationStream();

    // Cleanup on unmount
    return () => {
      console.log('🔔 useEffect cleanup - disconnecting from stream');
      disconnectFromNotificationStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Run when user becomes available

  // Fallback polling (only if SSE is not connected)
  useEffect(() => {
    if (!user || isConnected) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [user, isConnected, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    isConnected,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    connectToNotificationStream,
    disconnectFromNotificationStream,
  };

  return (
    <AgentNotificationContext.Provider value={value}>
      {children}
    </AgentNotificationContext.Provider>
  );
}; 